/**
 * MeasurementHistoryList Component
 *
 * Displays historical body measurements in reverse chronological order
 * with change indicators showing differences from previous entry.
 */

import React, { useState } from 'react';
import type { BodyMeasurement, WeightUnit, LengthUnit } from '../../types/measurements';
import { convertMeasurementValue } from '../../types/measurements';
import type { UnitSystem } from './UnitToggle';

interface MeasurementHistoryListProps {
  measurements: BodyMeasurement[];
  isLoading?: boolean;
  onEdit?: () => void;
  onDelete?: (measurementId: string) => void;
  displayUnit?: UnitSystem;
}

const MeasurementHistoryList: React.FC<MeasurementHistoryListProps> = ({
  measurements,
  isLoading = false,
  onEdit,
  onDelete,
  displayUnit = 'metric',
}) => {
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Helper to get target units based on display unit system
  const getTargetUnits = () => {
    return {
      weight: displayUnit === 'metric' ? 'kg' as WeightUnit : 'lbs' as WeightUnit,
      length: displayUnit === 'metric' ? 'cm' as LengthUnit : 'inches' as LengthUnit
    };
  };

  const targetUnits = getTargetUnits();
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-soft p-6">
        <h2 className="text-2xl font-serif font-bold text-primary-700 mb-4">
          Measurement History
        </h2>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-neutral-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (measurements.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-soft p-6">
        <h2 className="text-2xl font-serif font-bold text-primary-700 mb-4">
          Measurement History
        </h2>
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <p className="text-neutral-600 text-lg mb-2">No measurements yet</p>
          <p className="text-neutral-500 text-sm">
            Start by recording your first measurement above
          </p>
        </div>
      </div>
    );
  }

  const calculateChange = (current: number, previous: number | null): {
    value: number;
    direction: '↑' | '↓' | '→';
    color: string;
  } | null => {
    if (previous === null) return null;

    const difference = current - previous;
    const rounded = Math.round(difference * 10) / 10; // Round to 1 decimal

    if (Math.abs(rounded) < 0.1) {
      return { value: 0, direction: '→', color: 'text-neutral-500' };
    }

    return {
      value: Math.abs(rounded),
      direction: difference > 0 ? '↑' : '↓',
      color: difference > 0 ? 'text-red-600' : 'text-green-600',
    };
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dateOnly = date.toDateString();
    const todayOnly = today.toDateString();
    const yesterdayOnly = yesterday.toDateString();

    if (dateOnly === todayOnly) return 'Today';
    if (dateOnly === yesterdayOnly) return 'Yesterday';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isToday = (dateStr: string): boolean => {
    const measurementDate = new Date(dateStr).toDateString();
    const today = new Date().toDateString();
    return measurementDate === today;
  };

  const handleDeleteClick = (measurementId: string) => {
    setDeleteConfirmId(measurementId);
  };

  const handleDeleteConfirm = (measurementId: string) => {
    if (onDelete) {
      onDelete(measurementId);
    }
    setDeleteConfirmId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-soft p-6">
      <h2 className="text-2xl font-serif font-bold text-primary-700 mb-6">
        Measurement History
      </h2>

      <div className="space-y-4">
        {measurements.map((measurement, index) => {
          const previous = index < measurements.length - 1 ? measurements[index + 1] : null;

          const weightChange = calculateChange(
            measurement.measurements.weight.value,
            previous?.measurements.weight.value || null
          );

          const waistChange = calculateChange(
            measurement.measurements.waist.value,
            previous?.measurements.waist.value || null
          );

          const hipChange = calculateChange(
            measurement.measurements.hip.value,
            previous?.measurements.hip.value || null
          );

          return (
            <div
              key={measurement.id || measurement._id}
              className="border border-neutral-200 rounded-lg p-4 hover:border-primary-300 transition"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg text-neutral-800">
                    {formatDate(measurement.date)}
                  </h3>
                  <p className="text-xs text-neutral-500">
                    {new Date(measurement.created_at).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {index === 0 && (
                    <span className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded-full font-medium">
                      Latest
                    </span>
                  )}
                  {onEdit && isToday(measurement.date) && (
                    <button
                      onClick={onEdit}
                      className="px-3 py-1 text-xs font-medium rounded transition bg-primary-600 text-white hover:bg-primary-700"
                      title="Edit today's measurement"
                    >
                      Edit
                    </button>
                  )}
                  {onDelete && deleteConfirmId === (measurement.id || measurement._id) ? (
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleDeleteConfirm((measurement.id || measurement._id)!)}
                        className="px-3 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 transition"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={handleDeleteCancel}
                        className="px-3 py-1 text-xs font-medium bg-neutral-200 text-neutral-700 rounded hover:bg-neutral-300 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : onDelete ? (
                    <button
                      onClick={() => handleDeleteClick((measurement.id || measurement._id)!)}
                      className="px-3 py-1 text-xs font-medium bg-red-600 text-white rounded hover:bg-red-700 transition"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>

              {/* Required Measurements */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {/* Weight */}
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Weight</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-neutral-800">
                      {convertMeasurementValue(measurement.measurements.weight, targetUnits.weight).value}
                    </span>
                    <span className="text-sm text-neutral-600">
                      {targetUnits.weight}
                    </span>
                    {weightChange && (
                      <span className={`text-sm font-medium ${weightChange.color}`}>
                        {weightChange.direction === '→' ? '' : `${weightChange.value} `}
                        {weightChange.direction}
                      </span>
                    )}
                  </div>
                </div>

                {/* Height */}
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Height</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-neutral-800">
                      {convertMeasurementValue(measurement.measurements.height, targetUnits.length).value}
                    </span>
                    <span className="text-sm text-neutral-600">
                      {targetUnits.length}
                    </span>
                  </div>
                </div>

                {/* Chest */}
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Chest</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-neutral-800">
                      {convertMeasurementValue(measurement.measurements.chest, targetUnits.length).value}
                    </span>
                    <span className="text-sm text-neutral-600">
                      {targetUnits.length}
                    </span>
                  </div>
                </div>

                {/* Waist */}
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Waist</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-neutral-800">
                      {convertMeasurementValue(measurement.measurements.waist, targetUnits.length).value}
                    </span>
                    <span className="text-sm text-neutral-600">
                      {targetUnits.length}
                    </span>
                    {waistChange && (
                      <span className={`text-sm font-medium ${waistChange.color}`}>
                        {waistChange.direction === '→' ? '' : `${waistChange.value} `}
                        {waistChange.direction}
                      </span>
                    )}
                  </div>
                </div>

                {/* Hip */}
                <div>
                  <p className="text-xs text-neutral-600 mb-1">Hip</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-neutral-800">
                      {convertMeasurementValue(measurement.measurements.hip, targetUnits.length).value}
                    </span>
                    <span className="text-sm text-neutral-600">
                      {targetUnits.length}
                    </span>
                    {hipChange && (
                      <span className={`text-sm font-medium ${hipChange.color}`}>
                        {hipChange.direction === '→' ? '' : `${hipChange.value} `}
                        {hipChange.direction}
                      </span>
                    )}
                  </div>
                </div>

                {/* WHR (Waist-Hip Ratio) */}
                <div>
                  <p className="text-xs text-neutral-600 mb-1">WHR</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold text-primary-700">
                      {measurement.waist_hip_ratio.toFixed(2)}
                    </span>
                    <span className="text-xs text-neutral-500">
                      ratio
                    </span>
                  </div>
                </div>
              </div>

              {/* Optional Measurements - Only show if at least one is present */}
              {(measurement.measurements.left_arm ||
                measurement.measurements.right_arm ||
                measurement.measurements.left_thigh ||
                measurement.measurements.right_thigh) && (
                <div className="mt-4 pt-4 border-t border-neutral-100">
                  <p className="text-xs font-medium text-neutral-500 mb-3">
                    Additional Measurements
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* Left Arm */}
                    {measurement.measurements.left_arm && (
                      <div>
                        <p className="text-xs text-neutral-600 mb-1">Left Arm</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-semibold text-neutral-800">
                            {convertMeasurementValue(measurement.measurements.left_arm, targetUnits.length).value}
                          </span>
                          <span className="text-sm text-neutral-600">
                            {targetUnits.length}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Right Arm */}
                    {measurement.measurements.right_arm && (
                      <div>
                        <p className="text-xs text-neutral-600 mb-1">Right Arm</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-semibold text-neutral-800">
                            {convertMeasurementValue(measurement.measurements.right_arm, targetUnits.length).value}
                          </span>
                          <span className="text-sm text-neutral-600">
                            {targetUnits.length}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Left Thigh */}
                    {measurement.measurements.left_thigh && (
                      <div>
                        <p className="text-xs text-neutral-600 mb-1">Left Thigh</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-semibold text-neutral-800">
                            {convertMeasurementValue(measurement.measurements.left_thigh, targetUnits.length).value}
                          </span>
                          <span className="text-sm text-neutral-600">
                            {targetUnits.length}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Right Thigh */}
                    {measurement.measurements.right_thigh && (
                      <div>
                        <p className="text-xs text-neutral-600 mb-1">Right Thigh</p>
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-semibold text-neutral-800">
                            {convertMeasurementValue(measurement.measurements.right_thigh, targetUnits.length).value}
                          </span>
                          <span className="text-sm text-neutral-600">
                            {targetUnits.length}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {measurements.length >= 10 && (
        <p className="text-center text-sm text-neutral-500 mt-6">
          Showing {measurements.length} most recent measurements
        </p>
      )}
    </div>
  );
};

export default MeasurementHistoryList;
